/*
  cf-gate-fixes.js — Click Fencing, glass calculator (2026-08-18)

  Two fixes, both on GlassFencingCalculator.prototype. Self-installing, idempotent,
  every wrapper in try/catch so a display fix can never break the calculator.

  FIX 1 — "the Move / Middle buttons make the gate disappear".
    There is ONE shared #section-preview element for every section, but
    updateCalculationResults(n) ends with an unconditional renderSectionPreview(n).
    Pressing Move/Middle runs _applySmallGatePosition(), which correctly re-renders the
    section on screen and then calls updateQuoteDisplay(). updateQuoteDisplay() walks
    EVERY section and, for any whose cached config is missing or stale, calls
    updateCalculationResults(sNum) — so the last section calculated paints itself over
    the shared preview. With a 2-section job (Pete: 2 x 3620mm, gate on section 1) the
    gateless section 2 wins the race and the gate vanishes from the drawing while
    section.hasGate is still true. Navigating away and back "fixed" it, which is what
    made it look intermittent.

    Fix: the shared preview only ever paints the section the user is looking at.
    showSection() sets state.currentSection BEFORE it recalculates, so the legitimate
    render always passes the guard. Precedent already in ullc-calc.js (toggleGate:
    "if (this.state.currentSection === sectionNum) this.renderSectionPreview(...)").

  FIX 1b — "PerfMesh didn't draw this section" (Pete, 2026-08-21).
    FIX 1 as first shipped was too broad. blade.js, flat-top.js and perfmesh-calc.js all
    render EVERY section into a hidden off-screen div to build the Review & Checkout
    "Final Preview", so the guard blanked every section except the one last open on
    Configure. Pete saw an empty box where Section 1 of a 2-section PerfMesh quote should
    be; blade and flat top were broken the same way. Glass was never affected — ullc-calc.js
    builds its final preview through calculatePanelsLayout and never hijacks the id.

    Fix: stand the guard down while that hijack is in progress, detected by the
    #section-preview__bak element existing or by the element answering to #section-preview
    being the off-screen temp div at left:-9999px. Deliberate renders get through; racing
    on-screen ones still do not.

  FIX 2 — gate options were not fully reset when the gate is removed.
    toggleGate(n,false) cleared gateSide / gatePosition / gateMaxPos / wallGateEnd but
    left hingePanelSize, gateFillWall, gateHingeType, the Exact pin and the latch/hinge
    choices behind, so a re-added gate (or a new section length) inherited the previous
    gate's placement. Observed: a 1200mm hinge step set at 3000mm was still on the
    section at 4500mm after the gate had been toggled off and on.

    Fix: clear the whole gate option set — state AND the visible controls — before the
    original toggleGate runs its recalculation, so the fresh gate starts from defaults.
*/
(function () {
  'use strict';

  var TAG = '[cf-gate-fixes]';
  var MAX_TRIES = 120;   // ~60s at 500ms
  var tries = 0;

  function resetGateOptions(calc, sectionNum) {
    var section = calc && calc.state && calc.state.sections && calc.state.sections[sectionNum];
    if (!section) return;

    // ── state ──────────────────────────────────────────────────────────────
    section.gateSide      = 'left';
    section.gatePosition  = 9999;          // sentinel: re-centred on the next solve
    section.gateMaxPos    = undefined;
    section.wallGateEnd   = null;
    section.gateHingeType = 'glass-to-glass';
    section.gateFillWall  = false;
    section.gateAtRakeEnd = false;
    section._placeAtMiddle = true;
    delete section.hingePanelSize;         // small-gate hinge step
    delete section.smallGateModel;
    section.gateExactMode        = false;
    section.gateExactMeasurement = undefined;
    section.gateExactFromEnd     = 'left';
    section.latchType = 'glass-to-glass';
    section.hingeType = 'spring';
    section.hingeBase = false;
    try {
      if (typeof calc.getDefaultGateLength === 'function') {
        section.gateLength = String(calc.getDefaultGateLength('spring'));
      }
    } catch (e) {}

    // a pinned/positioned layout must not survive the reset
    try { delete calc.state.optimizedConfigs[sectionNum]; } catch (e) {}

    // ── visible controls ───────────────────────────────────────────────────
    var el = function (suffix) { return document.getElementById('section-' + sectionNum + '-' + suffix); };
    var set = function (node, prop, val) { if (node) node[prop] = val; };

    set(el('gate-side-left'),  'checked', true);
    set(el('gate-side-right'), 'checked', false);
    set(el('gate-hinge-type-glass'), 'checked', true);
    set(el('gate-hinge-type-wall'),  'checked', false);
    set(el('hinge-mount-toggle'), 'checked', false);
    var mountHint = el('hinge-mount-hint');
    if (mountHint) { mountHint.textContent = ''; mountHint.style.display = 'none'; }

    set(el('gate-exact-input'), 'value', '');
    set(el('gate-exact-end'),   'value', 'left');
    var exactHint = el('gate-exact-hint');
    if (exactHint) {
      exactHint.textContent = 'Enter the distance from the chosen end to the near edge of the gate, then press Exact. Using the Move buttons switches back to automatic placement.';
      exactHint.style.color = '#6b7280';
    }

    set(el('hinge-base'), 'checked', false);
    set(el('hinge-type-spring'), 'checked', true);
    set(el('hinge-type-soft-close'), 'checked', false);

    var latchGroup = el('latch-type-group');
    if (latchGroup) latchGroup.style.display = 'none';
    var latchSel = el('latch-type');
    if (latchSel) {
      try {
        if (typeof calc.generateLatchTypeOptions === 'function') {
          latchSel.innerHTML = calc.generateLatchTypeOptions(false);
        }
        var vals = Array.prototype.map.call(latchSel.options, function (o) { return o.value; });
        var g2g = vals.filter(function (v) { return /glass.?to.?glass/i.test(v); })[0] || vals[0];
        if (g2g) { latchSel.value = g2g; section.latchType = g2g; }
      } catch (e) {}
    }

    var gateLenSel = el('gate-length');
    if (gateLenSel && section.gateLength) {
      var has = Array.prototype.some.call(gateLenSel.options, function (o) { return o.value === String(section.gateLength); });
      if (has) gateLenSel.value = String(section.gateLength);
      else if (gateLenSel.options.length) { gateLenSel.selectedIndex = 0; section.gateLength = gateLenSel.value; }
    }
  }

  function install() {
    var gc = window.glassCalculator || window.glassFencingCalculator;
    if (!gc) return false;
    var proto = Object.getPrototypeOf(gc);
    if (!proto || typeof proto.renderSectionPreview !== 'function' || typeof proto.toggleGate !== 'function') return false;
    if (proto.__cfGateFixesPatched) return true;

    // ── FIX 1 ────────────────────────────────────────────────────────────────
    var origRender = proto.renderSectionPreview;

    // A deliberate off-screen render is not the race FIX 1 is guarding against.
    // blade.js, flat-top.js and perfmesh-calc.js build the Review & Checkout
    // "Final Preview" by creating a hidden temp div, handing it the #section-preview
    // id (the real element is renamed #section-preview__bak), calling
    // renderSectionPreview(n) for EVERY section and stealing the innerHTML.
    var HIJACK_STYLE_LEFT = '-9999px';
    function previewIsHijacked() {
      try {
        if (document.getElementById('section-preview__bak')) return true;
        var el = document.getElementById('section-preview');
        return !!(el && el.style && el.style.left === HIJACK_STYLE_LEFT);
      } catch (e) { return false; }
    }

    proto.renderSectionPreview = function (sectionNum) {
      try {
        if (!previewIsHijacked()) {
          var cur = this.state && this.state.currentSection;
          if (cur && sectionNum && Number(sectionNum) !== Number(cur)) return; // not the section on screen
        }
      } catch (e) {}
      return origRender.apply(this, arguments);
    };

    // ── FIX 2 ────────────────────────────────────────────────────────────────
    var origToggle = proto.toggleGate;
    proto.toggleGate = function (sectionNum, checked) {
      if (!checked) {
        try { resetGateOptions(this, sectionNum); } catch (e) { console.warn(TAG + ' reset failed:', e); }
      }
      return origToggle.apply(this, arguments);
    };

    proto.__cfGateFixesPatched = true;
    console.log(TAG + ' installed (shared-preview guard, hijack-aware + full gate reset)');
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) { console.warn(TAG + ' install error:', e); }
    if (done) return;
    if (++tries > MAX_TRIES) { console.warn(TAG + ' gave up after ' + tries + ' tries'); return; }
    setTimeout(tryInstall, 500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tryInstall);
  else tryInstall();
})();
